import { IsomorphicLogger, LogLevel, Appender, LogEntry } from './types'
import { ConsoleAppender, ServerAppender } from './appenders'
import { inferNamespace } from './namespaceUtil'
import { shouldLog } from './loggerConfig'

let loggerCache: Record<string, IsomorphicLogger> | null = null

class Logger implements IsomorphicLogger {
  private appenders: Appender[] = []

  constructor(private namespace: string) {
    if (typeof window === 'undefined') { // Server-side
      this.appenders.push(new ConsoleAppender())  //appends to the server console
    }
		else { // Client-side
      if (process.env.NODE_ENV !== 'production') {
        this.appenders.push(new ConsoleAppender()) //appends to the browser console
      }
			else {
        this.appenders.push(new ServerAppender())  //appends to the server via API
      }
    }
  }

  async log(level: LogLevel, message: string, context?: Record<string, any>): Promise<void> {
    if (shouldLog(this.namespace, level)) {
			const entry: LogEntry = {
				timestamp: new Date().toISOString(),
				level,
				namespace: this.namespace,
				message,
				...context
			}
			// console.log('iso logger', printObject(entry))
			await Promise.all(this.appenders.map(appender => appender.append(entry)))
		}
  }

  async trace(message: string, context?: Record<string, any>): Promise<void> {
    await this.log('trace', message, context)
  }

  async debug(message: string, context?: Record<string, any>): Promise<void> {
    await this.log('debug', message, context)
  }

  async info(message: string, context?: Record<string, any>): Promise<void> {
    await this.log('info', message, context)
  }

  async warn(message: string, context?: Record<string, any>): Promise<void> {
    await this.log('warn', message, context)
  }

  async error(message: string, context?: Record<string, any>): Promise<void> {
    await this.log('error', message, context)
  }

	isLoggingEnabled(level: LogLevel): boolean {
    return shouldLog(this.namespace, level)
	}

	isTraceEnabled(): boolean {
		return this.isLoggingEnabled('trace')
	}

	isDebugEnabled(): boolean {
		return this.isLoggingEnabled('debug')
	}

	isInfoEnabled(): boolean {
		return this.isLoggingEnabled('info')
	}

	isWarnEnabled(): boolean {
		return this.isLoggingEnabled('warn')
	}

	isErrorEnabled(): boolean {
		return this.isLoggingEnabled('error')
	}
}

function getLoggerCache(): Record<string, IsomorphicLogger> {
  if (!loggerCache) {
    loggerCache = {}
  }
  return loggerCache
}

export function createIsomorphicLogger(explicitNamespace?: string): IsomorphicLogger {
  const namespace = explicitNamespace || inferNamespace()
	const cache = getLoggerCache()

  if (!cache[namespace]) {
    cache[namespace] = new Logger(namespace)
  }
  return cache[namespace]
}